import moment from 'moment'
import {generate} from 'xksuid'

import i18n from '@/i18n'
import {BrowserDetection, DeviceDetection, OSDetection} from '@/types'
import {Lease, OpportunityType} from '@/types'
import {Lead, LeadSummary} from '@/types'
import {Material, MaterialModel, Types as Model} from '@/types/dynamodb-types'

type MessageFormat = 'plain' | 'html'

const formatNoAppointmentMessage = (message: string, messageFormat: MessageFormat, cssClass?: string): string => {
  if (messageFormat === 'html') {
    return `<span class="${cssClass}">${message}</span>`
  }
  return message
}

const formatFollowUpMessage = (message: string, messageFormat: MessageFormat, cssClass?: string): string => {
  if (messageFormat === 'html') {
    return `<span class="${cssClass}">${message}</span>`
  }
  return message
}

export const getIdMaterial = (str: string): any => {
  return str
    ?.match(/\d{6}-/)
    ?.shift()
    ?.substring(0, 6)
}

const firstNonZeroValue = (p: number, n: number) => {
  return p ? p : n
}

export const fieldSorter = (fields: string[]): any => {
  return (a: any, b: any) => {
    return fields
      .map((o: string) => {
        let dir = 1
        if (o[0] === '-') {
          // - => DESC sort
          dir = -1
          o = o.substring(1)
        }
        if (a[o] > b[o]) return dir
        if (a[o] < b[o]) return -dir
        return 0
      })
      .reduce(firstNonZeroValue, 0)
  }
}

export const capitalize = (str: string | null | undefined): string | null | undefined => {
  if (str === null || str === undefined) return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const toCapitalize = (str: string) => {
  const arr = str.split(' ')
  let res = ''
  arr.forEach((newStr, index) => {
    res += capitalize(newStr) + (index < arr.length - 1 ? ' ' : '')
  })
  return res
}
export const sanitizeForS3 = (str: string) => str.replace(/[^a-zA-Z\d:]/gm, '_')

function getOS(): OSDetection {
  const userAgent = navigator.userAgent
  const platform = Object.hasOwn(navigator, 'userAgentData')
    ? (navigator as any)?.userAgentData?.platform
    : window.navigator.platform

  const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K']
  const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE']
  const iosPlatforms = ['iPhone', 'iPad', 'iPod']

  let os: OSDetection = 'unknown'

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = 'mac'
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = 'ios'
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = 'windows'
  } else if (/Android/.test(userAgent)) {
    os = 'android'
  } else if (/Linux/.test(platform)) {
    os = 'linux'
  }

  return os
}

export function detectDevice(): DeviceDetection {
  const userAgent = navigator.userAgent
  const os = getOS()

  const isMobile = /iOS|Android/i.test(os ?? '')
  const isDesktop = !isMobile

  let browser: BrowserDetection = 'unknown'

  if (userAgent.match(/chrome|chromium|crios/i)) {
    browser = 'chrome'
  } else if (userAgent.match(/firefox|fxios/i)) {
    browser = 'firefox'
  } else if (userAgent.match(/safari/i)) {
    browser = 'safari'
  } else if (userAgent.match(/opr\//i)) {
    browser = 'opera'
  } else if (userAgent.match(/edg/i)) {
    browser = 'edge'
  }

  return {
    isDesktop,
    isMobile,
    browser,
    os,
  }
}

export function compactObject(data?: Record<string, any>): Record<string, any> | undefined {
  if (!data && typeof data !== 'object') {
    return data
  }

  return Object.keys(data).reduce(function (accumulator, key) {
    const isObject = data[key] !== null && typeof data[key] === 'object'
    let value: any = isObject ? compactObject(data[key]) : data[key]

    if (value === undefined) {
      // || isEmptyObject
      return accumulator
    }
    if (Array.isArray(data[key])) {
      value = Object.values(value)
    }

    return Object.assign(accumulator, {[key]: value})
  }, {})
}

export function generateUUID() {
  return generate()
}

export function createMaterialDescription(material: Material | undefined): string | undefined {
  if (!material) return undefined
  const year = material.model.y || material.model.ym || ''
  const color = material.color
    ? `${material.idMat}-${material.color.general}_${material.color.code}_${material.model.type}`
    : ''
  return `${year} ${color}`.trim()
}

export function createModelName(model: Model | MaterialModel | undefined): string | undefined {
  if (!model) return undefined
  const commercialName = model.name !== model.commercialName ? `${model.commercialName}` : ''
  return `${model.name} ${commercialName}`.trim()
}

export function buildFollowUpMessageText(
  locale: string,
  {
    appointmentDatetime,
    estimatedAppointment,
    regularCustomer,
  }: {
    appointmentDatetime?: string
    estimatedAppointment?: string
    regularCustomer?: boolean
  },
  format: {
    messageFormat: MessageFormat
    pendingAppointmentClass?: string
    messageClass?: string
  } = {
    messageFormat: 'plain',
    pendingAppointmentClass: '',
    messageClass: '',
  },
): string {
  const timeAt = i18n.t('leads.detail.follow-up.options.time-at')
  const localeDateFormat = `D/M/YYYY [${timeAt}] HH:mm`

  const regularCustomerLabel = regularCustomer
    ? i18n.t('leads.detail.follow-up.options.regular-customer-label')
    : i18n.t('leads.detail.follow-up.options.not-regular-customer-label')

  let result: string

  if (appointmentDatetime) {
    const formattedDate = moment(appointmentDatetime).format(localeDateFormat)
    const messageTemplate = `${i18n.t('leads.detail.follow-up.options.appointment', {
      date: formattedDate,
    })}\n${regularCustomerLabel}`

    result = estimatedAppointment ? `${messageTemplate}\n${estimatedAppointment}` : (messageTemplate as string)
  } else {
    const noAppointmentMessage = i18n.t('leads.detail.follow-up.options.no-appointment') as string
    const formattedNoAppointment = formatNoAppointmentMessage(
      noAppointmentMessage,
      format.messageFormat,
      format?.pendingAppointmentClass,
    )
    const base = `${formattedNoAppointment}\n${regularCustomerLabel}`

    result = estimatedAppointment ? `${base}\n${estimatedAppointment}` : (base as string)
  }

  return formatFollowUpMessage(result, format.messageFormat, format?.messageClass)
}

export function createInitialOptionsLeadCommentLog(locale: 'es' | 'en' | 'pt', lease: Lease, createdAt: string) {
  const now = moment()
  const leadCreatedAt = moment(createdAt, 'YYYY-MM-DD')

  return [
    {
      timestamp: now,
      dueDate: leadCreatedAt.startOf('day').toISOString(),
      text: buildFollowUpMessageText(locale, {
        appointmentDatetime: lease.appointmentDatetime,
        estimatedAppointment: lease.estimatedAppointment,
        regularCustomer: lease.regularCustomer,
      }),
      isRemovable: false,
    },
  ]
}

export function processOptionsLeadSummaryCommentLog(lead: LeadSummary, locale: 'es' | 'en' | 'pt'): LeadSummary {
  if (lead.type === OpportunityType.Options && lead.createdAt) {
    const leaseData = {
      appointmentDatetime: lead.appointmentDatetime,
      estimatedAppointment: lead.estimatedAppointment,
      regularCustomer: lead.regularCustomer,
    }

    const initialCommentLog = createInitialOptionsLeadCommentLog(locale, leaseData as Lease, lead.createdAt)
    return {
      ...lead,
      commentLog: [...(lead.commentLog || []), ...initialCommentLog],
    }
  }
  return lead
}

export function processOptionsLeadCommentLog(lead: Lead, locale: 'es' | 'en' | 'pt'): Lead {
  if (lead.opportunity?.type === OpportunityType.Options && lead.opportunity?.lease && lead.createdAt) {
    const initialCommentLog = createInitialOptionsLeadCommentLog(locale, lead.opportunity.lease, lead.createdAt)
    return {
      ...lead,
      commentLog: [...(lead.commentLog || []), ...initialCommentLog],
    }
  }
  return lead
}

export function hashString(str: string): string {
  let hash = 0
  if (str.length === 0) return hash.toString()

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash
  }

  return Math.abs(hash).toString(16)
}
